import { apiClient } from '@/lib';
import type { NotificationsResponse, UnreadCountResponse } from '@/types';

export const notificationsApi = {
  list: async (params?: { limit?: number; offset?: number }) => {
    const { data } = await apiClient.get<NotificationsResponse>('/notifications', { params });
    return data;
  },

  unreadCount: async () => {
    const { data } = await apiClient.get<UnreadCountResponse>('/notifications/unread-count');
    return data;
  },

  markAsRead: async (id: string) => {
    const { data } = await apiClient.patch(`/notifications/${id}/read`);
    return data;
  },

  markAllAsRead: async () => {
    const { data } = await apiClient.post('/notifications/mark-all-read');
    return data;
  },

  remove: async (id: string) => {
    const { data } = await apiClient.delete(`/notifications/${id}`);
    return data;
  },

  removeAll: async () => {
    const { data } = await apiClient.delete<{ count: number }>('/notifications');
    return data;
  },

  getRules: async () => {
    const { data } = await apiClient.get<NotificationRule[]>('/notifications/rules');
    return data;
  },

  updateRule: async (type: string, payload: { channels: string[]; isActive: boolean }) => {
    const { data } = await apiClient.put<NotificationRule>(`/notifications/rules/${type}`, payload);
    return data;
  },

  getVapidPublicKey: async () => {
    const { data } = await apiClient.get<{ publicKey: string }>('/notifications/vapid-public-key');
    return data;
  },

  getPushSubscription: async () => {
    const { data } = await apiClient.get<{ endpoint: string | null }>(
      '/notifications/push-subscription'
    );
    return data;
  },

  pushSubscribe: async (payload: { endpoint: string; keys: { p256dh: string; auth: string } }) => {
    const { data } = await apiClient.post('/notifications/push-subscribe', payload);
    return data;
  },

  pushUnsubscribe: async () => {
    const { data } = await apiClient.post('/notifications/push-unsubscribe');
    return data;
  },

  setEmailPreference: async (emailNotifications: boolean) => {
    const { data } = await apiClient.patch<{ emailNotifications: boolean }>(
      '/notifications/email-preference',
      { emailNotifications }
    );
    return data;
  },
};

export interface NotificationRule {
  id: string;
  tenantId: string;
  type: string;
  channels: string[];
  isActive: boolean;
}

export interface NotificationTemplate {
  id: string;
  tenantId: string;
  type: string;
  subject: string;
  bodyTemplate: string;
}

export const notificationTemplatesApi = {
  list: async () => {
    const { data } = await apiClient.get<NotificationTemplate[]>('/notifications/templates');
    return data;
  },

  upsert: async (type: string, payload: { subject: string; bodyTemplate: string }) => {
    const { data } = await apiClient.put<NotificationTemplate>(
      `/notifications/templates/${type}`,
      payload
    );
    return data;
  },

  remove: async (type: string) => {
    const { data } = await apiClient.delete(`/notifications/templates/${type}`);
    return data;
  },
};
